import { create } from 'zustand';
import { personasApiV2, type PersonaClient } from '@/services/api';

const COOKIE_KEY = 'ng_selected_client';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function readCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_KEY}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(value: string) {
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
}

function clearCookie() {
  document.cookie = `${COOKIE_KEY}=; path=/; max-age=0`;
}

interface ClientState {
  clients: PersonaClient[];
  selectedClientUid: string | null;
  loading: boolean;
  error: string | null;

  fetchClients: () => Promise<void>;
  selectClient: (clientUid: string | null) => void;

  /** Derive the currently selected PersonaClient object. */
  selectedClient: () => PersonaClient | null;
}

export const useClientStore = create<ClientState>((set, get) => ({
  clients: [],
  selectedClientUid: readCookie(),
  loading: false,
  error: null,

  fetchClients: async () => {
    set({ loading: true, error: null });
    try {
      const { clients } = await personasApiV2.listClients();
      set({ clients });

      // Re-validate cookie value against real client list
      const saved = get().selectedClientUid;
      const valid = clients.some((c) => c.client_uid === saved);
      if (!valid) {
        const first = clients[0]?.client_uid ?? null;
        set({ selectedClientUid: first });
        if (first) writeCookie(first);
        else clearCookie();
      }
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Failed to load clients' });
    } finally {
      set({ loading: false });
    }
  },

  selectClient: (clientUid) => {
    set({ selectedClientUid: clientUid });
    if (clientUid) writeCookie(clientUid);
    else clearCookie();
  },

  selectedClient: () => {
    const { clients, selectedClientUid } = get();
    return clients.find((c) => c.client_uid === selectedClientUid) ?? null;
  },
}));
